<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';

import { reactive, ref } from 'vue';
import { watchDebounced } from '@vueuse/core';
import _isEqual from 'lodash/isEqual';

import KNumberField from '@/components/fields/KNumberField.vue';
import useForm from '@/composables/useForm';
import { requiredValidationRule } from '@/helpers/validationRules';

export interface InitialData {
  width?: number;
  height?: number;
  length?: number;
}

export interface ValidData {
  width: number;
  height: number;
  length: number;
}

export interface Props {
  initialData?: InitialData;
}
const props = withDefaults(defineProps<Props>(), {
  initialData: () => ({}),
});
const emit = defineEmits<{
  submit: [event: SubmitEventPromise | null | undefined, formData: ValidData];
}>();

const formData = reactive<InitialData>({});
const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, function (event) {
  if (formData.width == null) return;
  if (formData.height == null) return;
  if (formData.length == null) return;
  const validData: ValidData = {
    width: formData.width,
    height: formData.height,
    length: formData.length,
  };
  emit('submit', event, validData);
});

const _isFirst = ref(true);

const volume = ref(0);

watchDebounced(
  [() => formData.width, () => formData.height, () => formData.length],
  async (newValues, oldValues) => {
    if (_isEqual(newValues, oldValues)) return;
    if (
      formData.width == null ||
      formData.height == null ||
      formData.length == null
    ) {
      volume.value = 0;
    } else {
      volume.value = formData.width * formData.height * formData.length;
    }
    if (_isFirst.value) {
      _isFirst.value = false;
      return;
    }
    await handleSubmit();
  },
  { deep: true, debounce: 100, immediate: true },
);
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.newAirRoadCalculateVolumeDialogForm"
    data-testid="newAirRoadCalculateVolumeDialogForm"
    @submit.prevent="handleSubmit"
  >
    <KNumberField
      v-model="formData.width"
      :min="0"
      size="xs"
      clearable
      label="Width"
      :rules="[requiredValidationRule]"
      :class="$style.field"
      class="mb-2"
      hide-details
      autofocus
    />
    <KNumberField
      v-model="formData.height"
      :min="0"
      size="xs"
      clearable
      label="Height"
      :rules="[requiredValidationRule]"
      :class="$style.field"
      class="mb-2"
      hide-details
    />
    <div class="d-flex align-center">
      <div class="w-100">
        <KNumberField
          v-model="formData.length"
          :min="0"
          size="xs"
          clearable
          label="Length"
          :rules="[requiredValidationRule]"
          :class="$style.field"
          hide-details
        />
      </div>
      <div class="text-nowrap">&nbsp;= {{ volume }} cm³</div>
    </div>
    <input type="submit" class="hidden" />
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.newAirRoadCalculateVolumeDialogForm {
  font-size: 14px;
}
  .field {
  }
</style>
